
import React, {
  useEffect,
  useMemo,
  useState,
} from "react";import {
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  AdsClick as AdsClickIcon,
  BarChart as AnalyticsIcon,
  Dashboard as DashboardIcon,
  DeviceHub as DeviceIcon,
  ExitToApp as ExitToAppIcon,
  ExpandLess,
  ExpandMore,
  Group as UsersIcon,
  Inventory2 as InventoryIcon,
  LocalOffer as OfferIcon,
  Person as ProfileIcon,
  QrCode2 as QrCode2Icon,
  Category as CategoryIcon,
  Settings as SettingsIcon,
  Storefront as StorefrontIcon,
  Tune as TuneIcon,
  BrandingWatermark as BrandIcon,
  NotificationsActive as NotificationsActiveIcon,
  ManageAccounts as ManageAccountsIcon,
  Upgrade as UpgradeIcon,
  ShoppingCart as ShoppingCartIcon,
  Warehouse as WarehouseIcon,
  SwapHoriz as SwapHorizIcon,
  BusinessCenter as SuppliersIcon,
  AccountBalanceWallet as SupplierBalancesIcon,
  ShoppingCartCheckout as PurchaseOrdersIcon,
  LocalShipping as GoodsReceiptsIcon,
} from "@mui/icons-material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import {
  useAuth,
} from "../contexts/AuthContext";
import {
  dashboardProfileApi,
} from "../api/dashboardProfileApi";

import type {
  DashboardProfile,
} from "../types/dashboard-profile.types";

interface SidebarProps {
  onLogout: () => void;
}

type MenuItem = {
  name: string;
  path: string;
  icon: React.ReactNode;

  permission?: string;

  hasSubmenu?: boolean;
  submenuKey?: string;
  submenu?: MenuItem[];
};

const drawerWidth = 272;

const Sidebar: React.FC<SidebarProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
const {
  hasPermission,
} = useAuth();
const [
  profile,
  setProfile,
] =
  useState<
    DashboardProfile |
    null
  >(null);

const [
  profileLoading,
  setProfileLoading,
] =
  useState(true);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({
    orders: false,
    alwaseet: false,
  });

  useEffect(() => {
  let mounted =
    true;

  const loadProfile =
    async () => {
      try {
        setProfileLoading(
          true,
        );

        const result =
          await dashboardProfileApi
            .getMe();

        if (!mounted) {
          return;
        }

        setProfile(
          result.profile,
        );
      } catch (
        error
      ) {
        console.error(
          "Failed to load sidebar profile:",
          error,
        );

        if (
          mounted
        ) {
          setProfile(
            null,
          );
        }
      } finally {
        if (
          mounted
        ) {
          setProfileLoading(
            false,
          );
        }
      }
    };

  loadProfile();

  return () => {
    mounted =
      false;
  };
}, []);

const profileRoleName =
  useMemo(
    () => {
      if (
        profile?.isSuperAdmin
      ) {
        return "Super Admin";
      }

      if (
        !profile?.role
      ) {
        return "بدون دور";
      }

      return (
        profile.role
          .nameAr ||
        profile.role.name
      );
    },
    [
      profile,
    ],
  );

const profileInitials =
  useMemo(
    () => {
      const name =
        profile?.fullName
          ?.trim() ||
        "";

      if (!name) {
        return "U";
      }

      const parts =
        name
          .split(/\s+/)
          .filter(Boolean);

      if (
        parts.length === 1
      ) {
        return parts[0]
          .slice(0, 2)
          .toUpperCase();
      }

      return `${
        parts[0]?.[0] ??
        ""
      }${
        parts[1]?.[0] ??
        ""
      }`.toUpperCase();
    },
    [
      profile?.fullName,
    ],
  );
  
const links:
  MenuItem[] =
  useMemo(
    () => {
      const allLinks:
        MenuItem[] = [
      { name: "الرئيسية", path: "/", icon: <DashboardIcon /> },
      {
  name:
    "طلبات ترقية الحساب",

  path:
    "/account-upgrade-requests",

  icon:
    <UpgradeIcon />,
},
      { name: "الحسابات", path: "/Account", icon: <DeviceIcon /> },
      

{
  name: "البراندات",
  path: "/brands",
  icon: <BrandIcon />,
},
{
  name: "التصنيفات",
  path: "/categories",
  icon: <CategoryIcon />,
},
{
  name: "الفلاتر",
  path: "/filter-options",
  icon: <TuneIcon />,
},



      { name: "إدارة المواد", path: "/Product", icon: <InventoryIcon /> },
      {
  name: "إدارة المخزون",
  path: "/inventory",
  icon: <InventoryIcon />,
},

{
  name: "إدارة المستودعات",
  path: "/warehouses",
  icon: <WarehouseIcon />,
  permission: "warehouses.view",
},

{
  name: "إدارة الموردين",
  path: "/suppliers",
  icon: <SuppliersIcon />,
  permission: "suppliers.view",
},

{
  name: "الأرصدة الافتتاحية للموردين",
  path: "/supplier-opening-balances",
  icon: <SupplierBalancesIcon />,
  permission:
    "supplier-opening-balances.view",
},

{
  name: "أوامر الشراء",
  path: "/purchase-orders",
  icon: <PurchaseOrdersIcon />,
  permission: "purchase-orders.view",
},


{
  name: "استلام البضاعة",
  path: "/goods-receipts",
  icon: <GoodsReceiptsIcon />,
  permission: "goods-receipts.view",
},

{
  name: "تحويلات المخزون",
  path: "/inventory-transfers",
  icon: <SwapHorizIcon />,
},

{
  name:
    "إدارة الإعلانات",

  path:
    "/advertisements",

  icon:
    <AdsClickIcon />,

  permission:
    "advertisements.view",
},
      { name: "الإعلانات", path: "/OffersList", icon: <StorefrontIcon /> },
      { name: "الفئات", path: "/Facet", icon: <AdsClickIcon /> },
      { name: "إدارة العلامات", path: "/tags", icon: <AnalyticsIcon /> },
      {
  name:
    "إدارة الطلبات",

  path:
    "/orders-admin",

  icon:
    <ShoppingCartIcon />,

  permission:
    "orders.view",
},
      {
        name: "الطلبات",
        path: "/CompleteOrdersList",
        icon: <ReceiptLongIcon />,
        hasSubmenu: true,
        submenuKey: "orders",
        submenu: [
          {
            name: "الطلبات غير المكتملة",
            path: "/uncomplete-orders",
            icon: <UsersIcon />,
          },
          {
            name: "الطلبات المكتملة",
            path: "/CompleteOrdersList",
            icon: <SettingsIcon />,
          },
        ],
      },
 
      {
        name: "الوسيط",
        path: "/alwaseet-orders",
        icon: <LocalShippingIcon />,
        hasSubmenu: true,
        submenuKey: "alwaseet",
        submenu: [
          {
            name: "طلبات الوسيط",
            path: "/alwaseet-orders",
            icon: <LocalShippingIcon />,
          },
          {
            name: "فواتير الوسيط",
            path: "/alwaseet-invoices",
            icon: <ReceiptLongIcon />,
          },
          {
            name: "حالات الوسيط",
            path: "/alwaseet-statuses",
            icon: <AssignmentTurnedInIcon />,
          },
        ],
      },
           {
  name:
    "حملات الإشعارات",

  path:
    "/notification-campaigns",

  icon:
    <NotificationsActiveIcon />,

  permission:
    "notifications.campaigns.view",
},
      {
        name: "إدارة سيريلات الوكلاء",
        path: "/dashboard/SerialAgent",
        icon: <QrCode2Icon />,
      },
      {
        name: "سيريلات الشركة",
        path: "/dashboard/company-serials",
        icon: <QrCode2Icon />,
      },
      {
        name: "سيريلات فعالة",
        path: "/dashboard/SerialNumber",
        icon: <QrCode2Icon />,
      },
      {
        name: "مقارنة السيريلات",
        path: "/dashboard/serial-comparison",
        icon: <QrCode2Icon />,
      },
      {
        name: "فلترة السيريلات",
        path: "/dashboard/MovementSerialFilter",
        icon: <QrCode2Icon />,
      },
      {
        name: "تعديل الموديلات",
        path: "/dashboard/SerialModelNameMapper",
        icon: <QrCode2Icon />,
      },
      {
  name: "إضافة سيريلات لمستخدم",
  path: "/dashboard/assign-user-serials",
  icon: <QrCode2Icon />,
},
     {
  name: "مستخدمو الداش بورد",
  path: "/dashboard-users",
  icon: <ManageAccountsIcon />,
  permission: "dashboard-users.view",
},
      {
        name: "عروض استبدال النقاط",
        path: "/dashboard/OffersPoint",
        icon: <OfferIcon />,
      },
      {
        name: "طلبات استبدال النقاط",
        path: "/dashboard/redeem-requests",
        icon: <ReceiptLongIcon />,
      },
      { name: "الملف الشخصي", path: "/profile", icon: <ProfileIcon /> },
         ];

      const filterItems =
        (
          items:
            MenuItem[],
        ): MenuItem[] =>
          items
            .filter(
              item =>
                !item.permission ||
                hasPermission(
                  item.permission,
                ),
            )
            .map(
              item => ({
                ...item,

                submenu:
                  item.submenu
                    ? filterItems(
                        item.submenu,
                      )
                    : undefined,
              }),
            );

      return filterItems(
        allLinks,
      );
    },
    [
      hasPermission,
    ],
  );

  const toggleMenu = (menuKey?: string) => {
    if (!menuKey) return;

    setOpenMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const isActivePath = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isParentActive = (item: MenuItem) => {
    if (isActivePath(item.path)) return true;
    return item.submenu?.some((subItem) => isActivePath(subItem.path)) ?? false;
  };

  const handleLogout = () => {
    onLogout();
    navigate("/login");
  };

  const renderMenuItem = (item: MenuItem, isSubItem = false) => {
    const active = isParentActive(item);

    if (item.hasSubmenu) {
      return (
        <React.Fragment key={item.name}>
          <ListItemButton
            onClick={() => toggleMenu(item.submenuKey)}
            selected={active}
            sx={menuItemSx(active, isSubItem)}
          >
            <ListItemIcon sx={menuIconSx(active, isSubItem)}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.name} primaryTypographyProps={menuTextProps} />
            {openMenus[item.submenuKey || ""] ? (
              <ExpandLess sx={{ color: active ? "#ffffff" : "#94a3b8" }} />
            ) : (
              <ExpandMore sx={{ color: active ? "#ffffff" : "#94a3b8" }} />
            )}
          </ListItemButton>

          <Collapse in={!!openMenus[item.submenuKey || ""]} timeout="auto" unmountOnExit>
            <List disablePadding sx={submenuContainerSx}>
              {item.submenu?.map((subItem) => renderMenuItem(subItem, true))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItemButton
        key={item.name}
        component={Link}
        to={item.path}
        selected={active}
        sx={menuItemSx(active, isSubItem)}
      >
        <ListItemIcon sx={menuIconSx(active, isSubItem)}>{item.icon}</ListItemIcon>
        <ListItemText primary={item.name} primaryTypographyProps={menuTextProps} />
      </ListItemButton>
    );
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", flexDirection: "row-reverse" }}>
      <Drawer
        variant="permanent"
        anchor="right"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            border: "none",
            color: "#ffffff",
            height: "100vh",
            right: 0,
            overflow: "hidden",
            background:
              "radial-gradient(circle at top right, rgba(61,117,133,0.55), transparent 34%), linear-gradient(180deg, #111827 0%, #1f2937 52%, #0f172a 100%)",
            boxShadow: "-16px 0 48px rgba(15, 23, 42, 0.18)",
          },
        }}
        open
      >
        <Box sx={sidebarRootSx}>
          <Box sx={brandBoxSx}>
            <Box sx={brandLogoSx}>S</Box>
            <Box>
              <Typography sx={brandTitleSx}>SHOOF Dashboard</Typography>
              <Typography sx={brandSubtitleSx}>لوحة إدارة التطبيق</Typography>
            </Box>
          </Box>

         <Box
  sx={{
    ...profileBoxSx,
    cursor: "pointer",
  }}
  onClick={() =>
    navigate(
      "/profile",
    )
  }
>
  <Avatar
    sx={
      avatarSx
    }
    alt={
      profile?.fullName ||
      "صورة المستخدم"
    }
    src={
      profile?.photoUrl ||
      undefined
    }
  >
    {!profile?.photoUrl &&
      !profileLoading &&
      profileInitials}
  </Avatar>

  <Box
    sx={{
      minWidth: 0,
      flex: 1,
    }}
  >
    <Typography
      sx={
        profileNameSx
      }
      noWrap
    >
      {profileLoading
        ? "جاري التحميل..."
        : profile?.fullName ||
          profile?.displayName ||
          "مستخدم الداش بورد"}
    </Typography>

    <Typography
      sx={
        profileRoleSx
      }
      noWrap
    >
      {profileLoading
        ? "..."
        : profileRoleName}
    </Typography>
  </Box>
</Box>

          <Typography sx={sectionTitleSx}>القائمة الرئيسية</Typography>

          <List sx={menuListSx}>{links.map((item) => renderMenuItem(item))}</List>

          <Box sx={logoutBoxSx}>
            <IconButton onClick={() => setLogoutDialogOpen(true)} sx={logoutButtonSx}>
              <ExitToAppIcon />
            </IconButton>
            <Box>
              <Typography sx={logoutTextSx}>تسجيل الخروج</Typography>
              <Typography sx={logoutSubTextSx}>إنهاء الجلسة الحالية</Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "24px",
            minWidth: { xs: "90%", sm: 420 },
            fontFamily: "Tajawal, sans-serif",
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "Tajawal, sans-serif", fontWeight: 800 }}>
          تأكيد تسجيل الخروج
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: "Tajawal, sans-serif", color: "#64748b" }}>
            هل أنت متأكد أنك تريد تسجيل الخروج من لوحة التحكم؟
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            sx={{ fontFamily: "Tajawal, sans-serif", borderRadius: "12px" }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleLogout}
            variant="contained"
            sx={{
              fontFamily: "Tajawal, sans-serif",
              borderRadius: "12px",
              background: "#ef4444",
              boxShadow: "none",
              "&:hover": { background: "#dc2626", boxShadow: "none" },
            }}
          >
            تسجيل الخروج
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const sidebarRootSx = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  p: "18px 14px",
  direction: "rtl",
};

const brandBoxSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: 1,
  mb: 2,
};

const brandLogoSx = {
  width: 46,
  height: 46,
  borderRadius: "16px",
  display: "grid",
  placeItems: "center",
  fontWeight: 900,
  color: "#111827",
  background: "linear-gradient(135deg, #ffffff, #dbeafe)",
  boxShadow: "0 12px 28px rgba(255,255,255,0.12)",
};

const brandTitleSx = {
  fontFamily: "Tajawal, sans-serif",
  fontWeight: 900,
  fontSize: 15,
  lineHeight: 1.2,
};

const brandSubtitleSx = {
  fontFamily: "Tajawal, sans-serif",
  fontSize: 12,
  color: "#94a3b8",
  mt: 0.4,
};

const profileBoxSx = {
  display:
    "flex",

  alignItems:
    "center",

  gap: 1.5,

  p: 1.4,

  mb: 2.4,

  borderRadius:
    "22px",

  background:
    "rgba(255,255,255,0.08)",

  border:
    "1px solid rgba(255,255,255,0.08)",

  transition:
    "0.22s ease",

  "&:hover": {
    background:
      "rgba(255,255,255,0.12)",

    borderColor:
      "rgba(255,255,255,0.14)",

    transform:
      "translateY(-1px)",
  },
};

const avatarSx = {
  width: 52,
  height: 52,

  flexShrink: 0,

  border:
    "3px solid rgba(255,255,255,0.18)",

  background:
    "linear-gradient(135deg, #3d7585, #1d4ed8)",

  color:
    "#ffffff",

  fontFamily:
    "Tajawal, sans-serif",

  fontWeight: 900,

  fontSize: 16,

  boxShadow:
    "0 8px 22px rgba(0,0,0,0.18)",
};

const profileNameSx = {
  fontFamily: "Tajawal, sans-serif",
  fontWeight: 800,
  fontSize: 15,
};

const profileRoleSx = {
  fontFamily: "Tajawal, sans-serif",
  color: "#94a3b8",
  fontSize: 12,
  mt: 0.3,
};

const sectionTitleSx = {
  px: 1.2,
  mb: 1,
  color: "#94a3b8",
  fontFamily: "Tajawal, sans-serif",
  fontWeight: 800,
  fontSize: 12,
};

const menuListSx = {
  flex: 1,
  overflowY: "auto",
  pr: 0.2,
  pl: 0.4,
  pb: 2,
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(255,255,255,0.18)",
    borderRadius: 999,
  },
};

const menuTextProps = {
  fontFamily: "Tajawal, sans-serif",
  fontSize: 13.5,
  fontWeight: 700,
};

const menuItemSx = (active: boolean, isSubItem: boolean) => ({
  minHeight: isSubItem ? 42 : 48,
  my: 0.35,
  mx: isSubItem ? 1 : 0,
  px: isSubItem ? 1.5 : 1.4,
  borderRadius: isSubItem ? "15px" : "18px",
  color: active ? "#ffffff" : "#cbd5e1",
  background: active
    ? "linear-gradient(135deg, rgba(61,117,133,0.95), rgba(37,99,235,0.35))"
    : "transparent",
  border: active ? "1px solid rgba(255,255,255,0.12)" : "1px solid transparent",
  transition: "0.22s ease",
  "&:hover": {
    background: active ? undefined : "rgba(255,255,255,0.08)",
    color: "#ffffff",
    transform: "translateX(-3px)",
  },
  "&.Mui-selected": {
    background: "linear-gradient(135deg, rgba(61,117,133,0.95), rgba(37,99,235,0.35))",
  },
  "&.Mui-selected:hover": {
    background: "linear-gradient(135deg, rgba(61,117,133,1), rgba(37,99,235,0.42))",
  },
});

const menuIconSx = (active: boolean, isSubItem: boolean) => ({
  minWidth: 38,
  color: active ? "#ffffff" : "#7dd3fc",
  "& svg": { fontSize: isSubItem ? 19 : 21 },
});

const submenuContainerSx = {
  my: 0.6,
  py: 0.4,
  borderRadius: "18px",
  background: "rgba(15,23,42,0.35)",
  border: "1px solid rgba(255,255,255,0.06)",
};

const logoutBoxSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.3,
  p: 1.2,
  mt: 1,
  borderRadius: "20px",
  background: "rgba(239,68,68,0.1)",
  border: "1px solid rgba(239,68,68,0.18)",
};

const logoutButtonSx = {
  width: 42,
  height: 42,
  color: "#ffffff",
  background: "rgba(239,68,68,0.88)",
  "&:hover": { background: "#dc2626" },
};

const logoutTextSx = {
  fontFamily: "Tajawal, sans-serif",
  color: "#ffffff",
  fontSize: 13,
  fontWeight: 800,
};

const logoutSubTextSx = {
  fontFamily: "Tajawal, sans-serif",
  color: "#fca5a5",
  fontSize: 11.5,
  mt: 0.2,
};

export default Sidebar;
